import React, { useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import type { Account } from '../types/api';
import { accountLabel } from '../utils/account-label';
import { useAuthStore } from '../stores/auth-store';
import {
  accountGroup, accountSearchText, ownerLabel, pickerAccounts,
} from '../utils/funding-accounts';

interface AccountSelectProps {
  label: string;
  accounts: Account[];
  /** Which account types to offer. */
  types: Account['account_type'][];
  value: string;
  onChange: (id: string) => void;
  helperText?: string;
  /** Show a "None" choice that clears the value. */
  allowNone?: boolean;
}

/**
 * A searchable account picker, grouped by kind, own accounts first.
 *
 * Replaces a plain select that offered whatever one page of accounts held.
 * With every household account present the list is long, so it is typed into
 * rather than scrolled: "5781", "wealthsimple" or "tia" each narrow it to the
 * right row.
 */
export const AccountSelect: React.FC<AccountSelectProps> = ({
  label, accounts, types, value, onChange, helperText, allowNone,
}) => {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const options = useMemo(
    () => pickerAccounts(accounts, types, currentUserId),
    [accounts, types, currentUserId],
  );
  const selected = options.find((a) => a.id === value) ?? null;

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={options}
      value={selected}
      onChange={(_, a) => onChange(a?.id ?? '')}
      groupBy={accountGroup}
      getOptionLabel={(a) => {
        const last4 = a.account_number_last4 && !accountLabel(a).includes(a.account_number_last4)
          ? ` ••${a.account_number_last4}` : '';
        return `${accountLabel(a)}${last4}`;
      }}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(opts, { inputValue }) => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return opts;
        const words = q.split(/\s+/);
        return opts.filter((a) => {
          const hay = accountSearchText(a, currentUserId);
          return words.every((w) => hay.includes(w));
        });
      }}
      renderOption={(props, a) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <Box component="li" key={key} {...rest}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{accountLabel(a)}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[a.institution, ownerLabel(a, currentUserId), a.currency].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          </Box>
        );
      }}
      clearOnEscape={allowNone}
      disableClearable={!allowNone}
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={helperText}
          placeholder="Search by name, bank, last 4 or owner" />
      )}
    />
  );
};
